
import { useState } from 'react';
import { savedItemsData } from '../../../mocks/saved-items';

export default function SavedItems() {
  const [savedProducts, setSavedProducts] = useState(savedItemsData.products);
  const [savedManufacturers, setSavedManufacturers] = useState(savedItemsData.manufacturers);
  const [activeTab, setActiveTab] = useState<'products' | 'manufacturers'>('products');

  const removeProduct = (id: string) => {
    setSavedProducts(savedProducts.filter(p => p.id !== id));
  };

  const removeManufacturer = (id: string) => {
    setSavedManufacturers(savedManufacturers.filter(m => m.id !== id));
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">관심 목록</h2>
        <p className="text-gray-600 text-lg">
          관심있는 제품과 제조업체를 저장하고 나중에 다시 확인하세요
        </p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-teal-700 mb-1">저장된 제품</div>
              <div className="text-3xl font-bold text-teal-900">{savedProducts.length}개</div>
            </div>
            <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center">
              <i className="ri-box-3-line text-white text-2xl"></i>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-cyan-700 mb-1">저장된 제조업체</div>
              <div className="text-3xl font-bold text-cyan-900">{savedManufacturers.length}개</div>
            </div>
            <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-building-line text-white text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-3 mb-8">
        <button
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
            activeTab === 'products'
              ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <i className="ri-box-3-line"></i>
          제품 ({savedProducts.length})
        </button>
        <button
          onClick={() => setActiveTab('manufacturers')}
          className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all whitespace-nowrap ${
            activeTab === 'manufacturers'
              ? 'bg-teal-600 text-white shadow-lg shadow-teal-600/30'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <i className="ri-building-line"></i>
          제조업체 ({savedManufacturers.length})
        </button>
      </div>

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          {savedProducts.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">저장된 제품이 없습니다</p>
            </div>
          ) : (
            savedProducts.map((product) => (
              <div key={product.id} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-32 h-32 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium mb-2">
                          {product.category}
                        </span>
                        <h3 className="text-xl font-semibold text-gray-900 mb-1">{product.name}</h3>
                        <p className="text-sm text-gray-600">{product.manufacturer}</p>
                      </div>
                      <button
                        onClick={() => removeProduct(product.id)}
                        className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <i className="ri-delete-bin-line text-xl"></i>
                      </button>
                    </div>
                    <p className="text-gray-600 mb-4">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-teal-600">{product.price}</div>
                      <div className="flex gap-2">
                        <button className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-all whitespace-nowrap">
                          <i className="ri-information-line"></i>
                          상세보기
                        </button>
                        <button className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-all whitespace-nowrap">
                          <i className="ri-shopping-cart-line"></i>
                          견적 요청
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Manufacturers Tab */}
      {activeTab === 'manufacturers' && (
        <div className="grid md:grid-cols-2 gap-6">
          {savedManufacturers.length === 0 ? (
            <div className="col-span-2 text-center py-16">
              <i className="ri-heart-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">저장된 제조업체가 없습니다</p>
            </div>
          ) : (
            savedManufacturers.map((manufacturer) => (
              <div key={manufacturer.id} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden">
                      <img
                        src={manufacturer.logo}
                        alt={manufacturer.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{manufacturer.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-map-pin-line"></i>
                        {manufacturer.location}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeManufacturer(manufacturer.id)}
                    className="w-10 h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <i className="ri-delete-bin-line text-xl"></i>
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                    {manufacturer.category}
                  </span>
                  <div className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-yellow-400"></i>
                    <span className="font-medium text-gray-900">{manufacturer.rating}</span>
                    <span className="text-gray-500">({manufacturer.reviews})</span>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">{manufacturer.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {manufacturer.specialties.map((specialty, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                      {specialty}
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200 transition-all whitespace-nowrap">
                    <i className="ri-building-line"></i>
                    업체 보기
                  </button>
                  <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-full text-sm font-medium hover:bg-teal-700 transition-all whitespace-nowrap">
                    <i className="ri-message-3-line"></i>
                    문의하기
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
